import { Dispatch, SetStateAction, useMemo } from 'react';

import { Column, ColumnFiltersState } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { Doc } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import { PopoverContent } from '@/new/components/shadcn/popover';

function FilterMultiSelect({
  values,
  options,
  column,
  placeholder,
}: {
  values: string[];
  options: string[];
  column: Column<Doc, unknown>;
  placeholder: string;
}) {
  return (
    <MultiSelect
      values={values}
      onValuesChange={(values) => {
        column.setFilterValue(values.length > 0 ? values : undefined);
      }}
    >
      <MultiSelectTrigger className="w-full">
        <MultiSelectValue placeholder={placeholder} />
      </MultiSelectTrigger>
      <MultiSelectContent>
        <MultiSelectGroup>
          {options
            .filter((type) => type) // remove falsey values
            .map((type) => (
              <MultiSelectItem key={type} value={type}>
                {type}
              </MultiSelectItem>
            ))}
        </MultiSelectGroup>
      </MultiSelectContent>
    </MultiSelect>
  );
}

export function FilterPopover({
  docs,
  typeColumn,
  extensionColumn,
  issuesColumn,
  setColumnFilters,
}: {
  docs: Doc[];
  typeColumn?: Column<Doc, unknown>;
  extensionColumn?: Column<Doc, unknown>;
  issuesColumn?: Column<Doc, unknown>;
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
}) {
  const { matterId } = useParams();
  if (!matterId) return null;

  const fileTypes = useMemo(() => Array.from(new Set(docs.map((doc) => doc.file.extension))), [docs]);
  const docTypes = useMemo(() => Array.from(new Set(docs.map((doc) => doc.type ?? ''))), [docs]);
  const issues = useMemo(() => Array.from(new Set(docs.map((doc) => doc.issues).flat())), [docs]);

  const typeFilterValue = typeColumn?.getFilterValue();
  const selectedTypes = Array.isArray(typeFilterValue) ? typeFilterValue : [];

  const extensionFilterValue = extensionColumn?.getFilterValue();
  const selectedExtensions = Array.isArray(extensionFilterValue) ? extensionFilterValue : [];

  const issuesFilterValue = issuesColumn?.getFilterValue();
  const selectedIssues = Array.isArray(issuesFilterValue) ? issuesFilterValue : [];

  return (
    <PopoverContent side="left" className="mt-12 w-lg" sideOffset={8}>
      <div className="-mx-4 -mt-4 rounded-t-md border-b bg-gray-50 p-4 font-medium text-gray-700">Filters</div>

      <div className="mt-4">
        {extensionColumn && (
          <>
            <p className="my-3 text-gray-600">File Extension</p>
            <FilterMultiSelect
              values={selectedExtensions}
              options={fileTypes}
              column={extensionColumn}
              placeholder="Select file extensions..."
            />
          </>
        )}
      </div>

      <div className="mt-4">
        {typeColumn && (
          <>
            <p className="my-3 text-gray-600">Document Type</p>
            <FilterMultiSelect
              values={selectedTypes}
              options={docTypes}
              column={typeColumn}
              placeholder="Select document types..."
            />
          </>
        )}
      </div>

      <div className="mt-4">
        {issuesColumn && (
          <>
            <p className="my-3 text-gray-600">Document Issues</p>
            <FilterMultiSelect
              values={selectedIssues}
              options={issues}
              column={issuesColumn}
              placeholder="Select document issues..."
            />
          </>
        )}
      </div>

      <div className="mt-4 flex w-full justify-end">
        <Button onClick={() => setColumnFilters([])}>
          <Icon className="lucide--funnel-x" />
          <span>Clear All Filters</span>
        </Button>
      </div>
    </PopoverContent>
  );
}
